/**
 * Événements — étape « Améliorer » du chapitre 2.
 *
 * Un événement survient après le déploiement : l'orage révèle l'intérêt d'une
 * supervision, une habitante se plaint de l'obscurité, la biodiversité revient.
 * Certains ouvrent un débat de classe, que l'interface affiche à part.
 */
import { CHAIN_ROLES } from './content-schema.js';
import type { ChapterId, Content, Effects, GameEvent } from './content-schema.js';
import { applyEffects } from './gauges.js';
import { chainEquipmentIds } from './chapter2.js';
import type { ActionRefusal, GameState } from './state.js';
import { refus, withAction } from './state.js';

/**
 * Les équipements que la ville possède réellement : ceux de la solution
 * déployée si un déploiement a eu lieu, sinon ceux posés dans la chaîne.
 */
export function installedEquipmentIds(state: GameState, content: Content): readonly string[] {
  if (state.deployedSolutionId) {
    const solution = content.solutions.find((s) => s.id === state.deployedSolutionId);
    if (solution) {
      return CHAIN_ROLES.flatMap((role) => solution.combinaison[role]);
    }
  }
  return chainEquipmentIds(state.chain);
}

/** La condition de déclenchement de l'événement est-elle remplie ? */
export function isEventEligible(state: GameState, event: GameEvent): boolean {
  const condition = event.condition;
  if (condition === null) {
    return true;
  }
  if ('solutions' in condition) {
    return (
      state.deployedSolutionId !== null && condition.solutions.includes(state.deployedSolutionId)
    );
  }
  return state.gauges.consommation <= condition.consommation_max;
}

/**
 * Les événements du chapitre qui peuvent survenir maintenant : bon chapitre,
 * pas encore joués, condition remplie.
 */
export function getEligibleEvents(
  state: GameState,
  content: Content,
  chapterId: ChapterId,
): readonly GameEvent[] {
  return content.evenements.filter(
    (event) =>
      event.chapitre === chapterId &&
      !state.triggeredEvents.includes(event.id) &&
      isEventEligible(state, event),
  );
}

export interface EventResolution {
  readonly event: GameEvent;
  /** Effets appliqués aux jauges, avant bornage. */
  readonly effets: Effects;
  /** Ce qu'il faut raconter à l'îlot. */
  readonly messages: readonly string[];
  /** La question à poser à toute la classe, s'il y en a une. */
  readonly debatClasse: string | null;
}

export type ResolveEventResult =
  | { readonly ok: true; readonly state: GameState; readonly resolution: EventResolution }
  | ActionRefusal;

/**
 * Joue un événement et applique ses effets.
 *
 * Trois formes, selon le contenu :
 * — `effets_si` : la première branche dont l'équipement est installé, sinon la
 *   branche « sinon » ;
 * — `choix` : l'îlot tranche, `choiceId` est alors obligatoire ;
 * — `effets` : l'événement s'applique tel quel.
 */
export function resolveEvent(
  state: GameState,
  content: Content,
  eventId: string,
  choiceId: string | null = null,
): ResolveEventResult {
  const event = content.evenements.find((e) => e.id === eventId);
  if (!event) {
    return refus('evenement_inconnu', `Événement absent du contenu : « ${eventId} ».`);
  }
  if (state.triggeredEvents.includes(eventId)) {
    return refus('evenement_deja_resolu', `L'événement « ${eventId} » a déjà été joué.`);
  }
  if (!isEventEligible(state, event)) {
    return refus(
      'evenement_non_eligible',
      `La condition de l'événement « ${eventId} » n'est pas remplie.`,
    );
  }

  let effets: Effects = {};
  const messages: string[] = [];
  let choixRetenu: string | null = null;

  if (event.effets_si) {
    const installes = installedEquipmentIds(state, content);
    const branche =
      event.effets_si.find(
        (candidate) =>
          'si_equipement_present' in candidate &&
          installes.includes(candidate.si_equipement_present),
      ) ?? event.effets_si.find((candidate) => 'sinon' in candidate);

    if (!branche) {
      return refus(
        'evenement_inconnu',
        `L'événement « ${eventId} » n'a aucune branche applicable.`,
      );
    }

    effets = branche.effets;
    messages.push(branche.message);
  } else if (event.choix) {
    if (choiceId === null) {
      return refus('choix_requis', `L'événement « ${eventId} » demande un choix à l'îlot.`);
    }
    const choix = event.choix.find((c) => c.id === choiceId);
    if (!choix) {
      return refus(
        'choix_inconnu',
        `L'événement « ${eventId} » n'offre pas le choix « ${choiceId} ».`,
      );
    }
    effets = choix.effets;
    choixRetenu = choix.id;
  } else if (event.effets) {
    effets = event.effets;
  }

  const avecEffets = applyEffects(state, effets, content);
  const eventChoices = choixRetenu
    ? { ...state.eventChoices, [eventId]: choixRetenu }
    : state.eventChoices;

  const state2: GameState = {
    ...avecEffets,
    triggeredEvents: [...state.triggeredEvents, eventId],
    eventChoices,
  };

  return {
    ok: true,
    state: withAction(state2, { type: 'resoudre_evenement', eventId, choiceId: choixRetenu }),
    resolution: {
      event,
      effets,
      messages,
      debatClasse: event.debat_classe ?? null,
    },
  };
}
